import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronUp, Plus, Eye, Trash2, Loader2, X, Play, Layers, Grid3X3, List, Edit } from 'lucide-react';
import _ from 'lodash';

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { TriggerModal } from '../components/TriggerModal';
import { useToast } from "../components/ui/Toast";

import { Playbook } from '../types';
import { usePlaybooksApi } from '../apis/playbooks-apis';

const ITEMS_PER_PAGE = 20;

export const PlaybooksList: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const playbooksApi = usePlaybooksApi();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(false);

  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [displayedPlaybooks, setDisplayedPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Playbook[] | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playbookToDelete, setPlaybookToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  // Trigger modal state
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState<Playbook | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const getAllPlaybooks = false;
        const fetchedPlaybooks = await playbooksApi.getPlaybooks(getAllPlaybooks);

        if (isMounted.current) {
          setPlaybooks(fetchedPlaybooks);
          setTotalCount(fetchedPlaybooks.length);
          setDisplayedPlaybooks(fetchedPlaybooks.slice(0, ITEMS_PER_PAGE));
          setHasMore(fetchedPlaybooks.length > ITEMS_PER_PAGE);
        }
      } catch (error: unknown) {
        if (isMounted.current) {
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching playbooks:', error);
            addToast({
              title: "Error",
              description: "Failed to retrieve playbooks. Please try again.",
              variant: "destructive",
            });
          }
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    isMounted.current = true;
    fetchInitialData();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, []);

  const loadMorePlaybooks = useCallback(() => {
    if (searchTerm || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const startIndex = displayedPlaybooks.length;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    if (startIndex >= playbooks.length) {
      setHasMore(false);
      setLoadingMore(false);
      return;
    }

    const newItems = playbooks.slice(startIndex, endIndex);
    setDisplayedPlaybooks(prev => [...prev, ...newItems]);

    if (endIndex >= playbooks.length) {
      setHasMore(false);
    }

    setLoadingMore(false);
  }, [searchTerm, loadingMore, hasMore, playbooks, displayedPlaybooks.length]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !searchTerm) {
        loadMorePlaybooks();
      }
    }, options);

    const loader = document.querySelector('#scroll-loader');
    if (loader) {
      observer.observe(loader);
    }

    return () => observer.disconnect();
  }, [loadMorePlaybooks, hasMore, loadingMore, searchTerm]);

  const handleSearch = _.debounce((term: string) => {
    setSearchTerm(term);
    if (term) {
      const results = playbooks.filter(playbook =>
        playbook.playbookName.toLowerCase().includes(term.toLowerCase()) ||
        (playbook.playbookProductName && playbook.playbookProductName.toLowerCase().includes(term.toLowerCase())) ||
        (playbook.playbookDescription?.toLowerCase().includes(term.toLowerCase())) ||
        (playbook.playbookTags?.some(tag => tag.toLowerCase().includes(term.toLowerCase())))
      );
      setSearchResults(results);
    } else {
      setSearchResults(null);
      setDisplayedPlaybooks(playbooks.slice(0, ITEMS_PER_PAGE));
      setHasMore(playbooks.length > ITEMS_PER_PAGE);
    }
  }, 300);

  const handleViewPlaybook = (playbookId: string) => {
    navigate(`/studio/playbooks/view/${playbookId}`);
  };

  const handleEditPlaybook = (playbookId: string) => {
    navigate(`/studio/playbooks/edit/${playbookId}`);
  };

  const handleTriggerClick = (playbook: Playbook) => {
    setSelectedPlaybook(playbook);
    setTriggerModalOpen(true);
  };

  const handleTriggerSubmit = async (playbookId: string, inputData: string) => {
    await playbooksApi.triggerPlaybook(playbookId, inputData);
  };

  const handleDeleteClick = (id: string) => {
    setPlaybookToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!playbookToDelete) return;

    setIsDeletingId(playbookToDelete);
    try {
      await playbooksApi.deletePlaybook(playbookToDelete);
      addToast({
        title: "Success",
        description: "Playbook successfully deleted",
        variant: "default",
      });

      try {
        const getAllPlaybooks = false;
        const fetchedPlaybooks = await playbooksApi.getPlaybooks(getAllPlaybooks);
        setPlaybooks(fetchedPlaybooks);
        setTotalCount(fetchedPlaybooks.length);
        setDisplayedPlaybooks(fetchedPlaybooks.slice(0, ITEMS_PER_PAGE));
        setHasMore(fetchedPlaybooks.length > ITEMS_PER_PAGE);
      } catch (error) {
        console.error('Error refreshing playbooks:', error);
      }
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to delete playbook. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setPlaybookToDelete(null);
    }
  };

  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setShowScrollTop(window.scrollY > 400);
    }, 200);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentPlaybooks = searchResults || displayedPlaybooks;
  const currentTotal = searchResults ? searchResults.length : totalCount;

  const renderPlaybookCard = (pkg: Playbook) => (
              <Card key={pkg.playbookId} className="group hover:shadow-xl transition-all duration-300 border-sky-200 hover:border-sky-400 hover:scale-[1.02] relative ring-2 ring-transparent hover:ring-sky-200/50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          {/* Left side - Icon and Info */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
                         {/* Playbook Icon */}
             <div className="bg-gradient-to-br from-sky-100 to-cyan-200 p-3 rounded-xl flex-shrink-0 shadow-sm border border-sky-100">
               <Layers className="h-6 w-6 text-sky-600" />
             </div>
            
            {/* Playbook Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                                 <h3 className="text-lg font-semibold text-gray-900 group-hover:text-sky-600 transition-colors truncate tracking-tight">
                   {pkg.playbookName}
                 </h3>
                {pkg.playbookProductName && (
                                     <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-sky-400 to-cyan-500 text-white shadow-sm flex-shrink-0">
                     {pkg.playbookProductName}
                   </span>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mb-2"
                 style={{
                   display: '-webkit-box',
                   WebkitLineClamp: 2,
                   WebkitBoxOrient: 'vertical' as const,
                   overflow: 'hidden'
                 }}>
                {pkg.playbookDescription || 'No description provided.'}
              </p>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleViewPlaybook(pkg.playbookId)}
              className="h-8 w-8 p-0 hover:bg-sky-50 hover:text-sky-600"
              title="View Playbook"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEditPlaybook(pkg.playbookId)}
              className="h-8 w-8 p-0 hover:bg-orange-50 hover:text-orange-600"
              title="Edit Playbook"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTriggerClick(pkg)}
              className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600"
              title="Trigger Playbook"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Play className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteClick(pkg.playbookId)}
              className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
              disabled={isDeletingId === pkg.playbookId}
              title="Delete Playbook"
            >
              {isDeletingId === pkg.playbookId ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Bottom section - Tags */}
        {pkg.playbookTags && pkg.playbookTags.length > 0 && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex flex-wrap gap-1">
              {pkg.playbookTags.slice(0, 4).map((tag, index) => (
                <span
                  key={index}
                                     className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors"
                >
                  {tag}
                </span>
              ))}
              {pkg.playbookTags.length > 4 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  +{pkg.playbookTags.length - 4} more
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderPlaybookListItem = (pkg: Playbook) => (
    <Card key={pkg.playbookId} className="mb-3 hover:shadow-lg transition-all duration-200 hover:border-blue-300 group">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Playbook Icon */}
            <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-2 rounded-lg flex-shrink-0">
              <Layers className="h-5 w-5 text-blue-600" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900 truncate tracking-tight">
                  {pkg.playbookName}
                </h3>
                <div className="flex items-center gap-2">
                  {pkg.playbookProductName && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-sm">
                      {pkg.playbookProductName}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-1 truncate">
                {pkg.playbookDescription || 'No description provided.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewPlaybook(pkg.playbookId)}
              className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Eye className="h-4 w-4 mr-1" />
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEditPlaybook(pkg.playbookId)}
              className="border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleTriggerClick(pkg)}
              className="border-green-200 text-green-600 hover:bg-green-50 hover:border-green-300"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Play className="h-4 w-4 mr-1" />
              Trigger
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteClick(pkg.playbookId)}
              className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
              disabled={isDeletingId === pkg.playbookId}
            >
              {isDeletingId === pkg.playbookId ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Calculate stats
  const stats = {
    total: playbooks.length,
    active: currentPlaybooks.length,
    products: new Set(playbooks.filter(p => p.playbookProductName).map(p => p.playbookProductName)).size,
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-7xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased" ref={containerRef}>
      {/* Hero Header Section */}
      <div className="relative mb-8 overflow-hidden">
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600 via-blue-500 to-cyan-700"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-sky-500/30 via-transparent to-cyan-500/30"></div>
        
        {/* Animated background elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-sky-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-gradient-to-r from-white/10 to-transparent"></div>
        
        {/* Content */}
        <div className="relative z-10 px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl shadow-lg self-start">
                  <Layers className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2 drop-shadow-lg">
                    Workflow Playbooks
                  </h1>
                  <p className="text-sky-100 text-base sm:text-lg font-medium max-w-2xl leading-relaxed">
                    Design and execute automated workflows that orchestrate your agents to complete complex multi-step processes
                  </p>
                </div>
              </div>
              
              {/* Live Stats */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-6">
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-white">{stats.total}</span> Total Playbooks
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-cyan-200">{stats.active}</span> Available
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-sky-200">{stats.products}</span> Products
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/studio/playbooks/add')}
                className="group relative bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold text-base sm:text-lg w-full sm:w-auto"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-xl group-hover:bg-white/30 transition-colors">
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span>Create Playbook</span>
                </div>
                
                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search playbooks by name, description, or tags..."
              className="pl-10 pr-4 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                handleSearch(e.target.value);
              }}
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  handleSearch('');
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center">
          <div className="flex items-center border border-gray-300 rounded-lg bg-white">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-600 font-medium">
          {searchTerm ? (
            <>Found {currentPlaybooks.length || 0} results for "{searchTerm}"</>
          ) : (
            <>Showing {currentPlaybooks.length} of {currentTotal} playbooks</>
          )}
        </p>
      </div>

      {/* Playbooks Grid/List */}
      <div className={
        viewMode === 'grid' 
          ? 'grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2' 
          : 'space-y-3'
      }>
        {currentPlaybooks.map(playbook => 
          viewMode === 'grid' ? renderPlaybookCard(playbook) : renderPlaybookListItem(playbook)
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <Spinner className="w-8 h-8 text-blue-500" />
          <p className="text-gray-600">Loading playbooks...</p>
        </div>
      ) : currentPlaybooks.length === 0 && (
        <Card className="p-12 text-center border-2 border-dashed border-gray-300">
          <div className="flex flex-col items-center gap-4">
            <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-4 rounded-full">
              <Layers className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 tracking-tight font-['Radley','serif']">
                {searchTerm ? 'No playbooks found' : 'No playbooks yet'}
              </h3>
              <p className="text-gray-600 mb-4 max-w-md">
                {searchTerm 
                  ? 'Try adjusting your search to find what you\'re looking for.'
                  : 'Get started by creating your first playbook to orchestrate automated workflows with your agents.'
                }
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => navigate('/studio/playbooks/add')}
                  className="group relative bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-4 rounded-xl font-semibold text-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg group-hover:bg-white/30 transition-colors">
                      <Plus className="h-5 w-5" />
                    </div>
                    <span>Create Your First Playbook</span>
                  </div>
                  
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10 blur-xl"></div>
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Playbook"
        description="This will permanently delete the playbook from the system. This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />

      {selectedPlaybook && (
        <TriggerModal
          isOpen={triggerModalOpen}
          onClose={() => setTriggerModalOpen(false)}
          playbookId={selectedPlaybook.playbookId}
          playbookName={selectedPlaybook.playbookName}
          onSubmit={handleTriggerSubmit}
        />
      )}

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white"
        >
          <ChevronUp size={24} />
        </Button>
      )}
    </div>
  );
};

export default PlaybooksList;